import React from 'react';
import { motion } from 'framer-motion';
import NotificationForm from '../NotificationForm';
import SocialLinks from '../SocialLinks';
import SwiperCardSlider from '../SwiperCardSlider';

const Home: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-full relative"
    >
      {/* HeroSection - Simple, elegant design with proper spacing */}
      <section className="min-h-[90vh] flex items-center relative overflow-hidden pt-0 pb-8">
        
        {/* Content container with two-column layout for content and swiper */}
        <div className="container mx-auto relative z-10 h-full flex items-center px-4 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center w-full">
            {/* Left column: Content */}
            <div className="space-y-8 md:space-y-12 md:text-left text-center">
          
            {/* Main heading with elegant styling */}
            <motion.h1 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              <div className="relative inline-block">
                <span className="block text-6xl md:text-7xl lg:text-8xl font-bold text-gray-800 tracking-tight">SokoVibe</span>
                <motion.div 
                  className="absolute -bottom-2 left-0 w-0 h-0.5 bg-gray-800 rounded-full"
                  animate={{ width: "100%" }}
                  transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
                ></motion.div>
              </div>
              <span className="block text-2xl md:text-3xl lg:text-4xl font-light text-gray-600 tracking-wide">Afro-Caribbean Marketplace</span>
            </motion.h1>
          
            {/* Tagline with elegant styling */}
            <motion.p 
              className="text-xl md:text-2xl font-light text-gray-700"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Feel the Rhythm. Taste the Culture.
            </motion.p>
          
            {/* Description with increased spacing */}
            <motion.div 
              className="max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              <p className="text-lg text-gray-600 leading-relaxed">
                We're excited to introduce you to <span className="font-medium">SokoVibe</span> – a brand-new online marketplace built specifically for Afro-Caribbean businesses in the diaspora.
              </p>
            </motion.div>
            
            {/* Coming Soon message with social links */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="pt-6 space-y-6"
            >
              {/* Unique coming soon message */}
              <div className="md:text-left text-center w-full">
                <div className="relative inline-flex items-center">
                  <div className="h-px w-8 bg-gray-400 mr-3"></div>
                  <div className="relative">
                    <motion.span 
                      className="text-lg font-medium tracking-wide text-gray-700"
                      animate={{ opacity: [0.7, 1, 0.7] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    >
                      Launching<span className="inline-flex space-x-1 ml-2">
                        <motion.span 
                          animate={{ opacity: [0, 1, 0] }} 
                          transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 0.5 }}
                        >.</motion.span>
                        <motion.span 
                          animate={{ opacity: [0, 1, 0] }} 
                          transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 0.5, delay: 0.5 }}
                        >.</motion.span>
                        <motion.span 
                          animate={{ opacity: [0, 1, 0] }} 
                          transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 0.5, delay: 1 }}
                        >.</motion.span>
                      </span>
                    </motion.span>
                    <span className="ml-2 text-xs text-gray-500 italic">very soon</span>
                  </div>
                </div>
              </div>
              
              {/* Follow for updates text */}
              <div className="text-sm text-gray-500 md:text-left text-center">
                Follow for updates
              </div>
              
              {/* Social links */}
              <SocialLinks />
            </motion.div>
            </div>
            
            {/* Swiper - visible on all devices */}
            <div className="relative order-first md:order-last mb-8 md:mb-0">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="flex items-center justify-center h-full"
              >
                <div className="w-[280px] h-[560px] md:w-[320px] md:h-[640px] transform scale-90 md:scale-95 lg:scale-100">
                  <SwiperCardSlider 
                    images={[
                      "/assets/logo.png",
                      "/assets/screenshot1.png",
                      "/assets/screenshot2.png",
                      "/assets/screenshot3.png",
                      "/assets/screenshot4.png"
                    ]} 
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
        
        {/* Simplified scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border border-gray-300 rounded-full flex justify-center pt-1">
            <motion.div 
              className="w-1 h-2 bg-gray-300 rounded-full"
              animate={{ y: [0, 4, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            ></motion.div>
          </div>
        </motion.div>
      </section>
      
      {/* Message Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-3xl mx-auto space-y-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-800 via-orange-500 to-yellow-500 mb-6">Our Vision</h2>
            
            <div className="pl-4 border-l-2 border-orange-500">
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                From bold artwork to vibrant food, soulful books, natural beauty products, and more – SokoVibe is where authentic Afro-Caribbean culture meets global shoppers who value it.
              </p>
              
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Whether you're an independent maker, a growing brand, or a seasoned vendor, we're here to connect your products with customers across the UK, Europe, North America, and beyond – people who are actively seeking what you offer.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
    </motion.div>
  );
};

export default Home;
